
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '@/config';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext'; // Import the useAuth hook
import { Loader2, CheckCircle, AlertTriangle } from 'lucide-react';

const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { notify } = useToast();
  const { setCurrentUser } = useAuth(); // Get the setCurrentUser function

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenParam = searchParams.get('token');
    const emailParam = searchParams.get('email');
    setToken(tokenParam);
    setEmail(emailParam);

    if (!tokenParam || !emailParam) {
      setError('Invalid reset link. Token or email is missing.');
    }
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
        setError('Password must be at least 8 characters long.');
        return;
    }
    if (!token || !email) {
      setError('Missing required information from the reset link.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to reset password.');
      }
      
      // --- SUCCESS AND AUTO-LOGIN ---
      setSuccess(data.message);
      notify('Password reset successfully! You are now being logged in.', 'success');

      // Use the data returned from the backend to log the user in
      if (data.user && data.token) {
        setCurrentUser(data.user);
        // Optionally, you might want to store the token in localStorage/sessionStorage
        // depending on your session management strategy.
      }

      // Redirect to the dashboard after a short delay
      setTimeout(() => navigate('/dashboard'), 2000);

    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(message);
      notify(message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-kala-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-kala-800 p-8 rounded-xl border border-kala-700">
        <h1 className="text-3xl font-bold text-white text-center mb-6">Reset Your Password</h1>
        
        {error && (
          <div className="bg-red-900/20 border border-red-500/30 p-4 rounded-lg mb-6 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        )}

        {success ? (
          <div className="bg-green-900/20 border border-green-500/30 p-4 rounded-lg mb-6 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <div>
                <p className="text-green-200 font-bold">{success}</p>
                <p className="text-green-300 text-sm">Redirecting you to the dashboard...</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-kala-400 mb-2" htmlFor="password">
                New Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-kala-900 border border-kala-600 rounded-lg p-3 text-white text-sm outline-none focus:border-kala-secondary focus:ring-1 focus:ring-kala-secondary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-kala-400 mb-2" htmlFor="confirmPassword">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full bg-kala-900 border border-kala-600 rounded-lg p-3 text-white text-sm outline-none focus:border-kala-secondary focus:ring-1 focus:ring-kala-secondary"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting || !token || !email}
              className="w-full px-8 py-3 bg-kala-secondary hover:bg-cyan-400 text-kala-900 font-bold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
